/**
 * Wind triangle maths for the wind-triangle tool.
 *
 * Ports computeTrueWind from libs/utilities/utilities/wind_utils.py.
 * Boat frame: +X forward, +Y starboard.
 *
 * Pure functions only — no DOM, no Solid. Everything here is covered by
 * frontend/__tests__/unit/windTriangleMath.test.ts.
 */

export type Tack = "port" | "stbd";
export type CorrectionName = "bow" | "mhu";

/** Why a mast rotation could not be fitted, or that it was. */
export type MastState =
  | "fitted"
  | "unidentifiable"
  | "unreachable"
  | "noSplitSolution"
  | "notApplicable";

export interface CorrectionMode {
  offsetLabel: string;
  fitMast: boolean;
  target: string;
}

/**
 * Where the wind sensor lives decides which biases are physically available.
 *
 *   bow — fixed to the hull. One free parameter: a constant AWA offset.
 *   mhu — rides the rotating mast, so mast rotation is a second parameter on
 *         top of that constant offset.
 *
 * The two parameters are not interchangeable, and this is the crux of the
 * whole fit. Written in terms of |AWA| per tack:
 *
 *   |awa_stbd| = |awa| + offset + mast
 *   |awa_port| = |awa| − offset + mast
 *
 * The offset is ANTI-symmetric across tacks — it opens a split of exactly
 * 2·offset — so a port/stbd pair pins it down cleanly.
 *
 * Mast rotation is SYMMETRIC: it shifts |AWA| by the same amount on both
 * tacks. It therefore reaches the tack split ONLY through whatever differs
 * between the tacks, which is the boat speed and the wind speed each one saw.
 * Sail both tacks at the same speed and its influence on the split is exactly
 * zero — unidentifiable, no weighting can rescue it. Sail them at different
 * speeds and it becomes identifiable but poorly conditioned, so a small AWS
 * error is amplified into a large bogus rotation. Either way the honest thing
 * is to report how well determined it actually is.
 *
 * A reference TWS sidesteps all of that: mast rotation moves the absolute TWS
 * level strongly and directly, so when a trusted TWS is available it is much
 * the better observable.
 */
export const CORRECTION_MODES: Record<CorrectionName, CorrectionMode> = {
  bow: {
    offsetLabel: "Bow offset",
    fitMast: false,
    target: "Δ|TWA| → 0",
  },
  mhu: {
    offsetLabel: "MHU offset",
    fitMast: true,
    target: "Δ|TWA| → 0, plus TWS",
  },
};

export function correctionMode(name: string | undefined): CorrectionMode {
  return CORRECTION_MODES[name as CorrectionName] || CORRECTION_MODES.mhu;
}

/**
 * Bias beyond this is not a calibration offset any more, it is a wiring or
 * convention error, and reporting a "fit" would only hide that.
 */
export const BIAS_LIMIT_DEG = 30;

/**
 * Speed error assumed when quoting how well the mast rotation is pinned. Not a
 * tuning knob for the fit — it only scales the reported uncertainty.
 */
export const TWS_UNCERTAINTY = 0.1;

/** Above this the mast rotation is too loosely determined to act on. */
export const MAST_UNCERTAINTY_LIMIT_DEG = 1;

// ---------------------------------------------------------------------------
// Angle helpers (mirror math_utils wrap / add)
// ---------------------------------------------------------------------------

export function wrap360(deg: number): number {
  let d = deg % 360;
  if (d < 0) {d += 360;}
  return d;
}

export function wrap180(deg: number): number {
  const d = ((((deg + 180) % 360) + 360) % 360) - 180;
  if (d === -180) {return 180;}
  return d;
}

export function angleAdd(a: number, b: number): number {
  return wrap360(a + b);
}

export function angleSubtract(a: number, b: number): number {
  return wrap180(a - b);
}

export function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function toDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

export function signedAwa(awa: number): number {
  if (awa > 180) {return wrap180(awa - 360);}
  return awa;
}

// ---------------------------------------------------------------------------
// The triangle
// ---------------------------------------------------------------------------

export interface TrueWind {
  tws: number;
  twa: number;
  twd: number;
  vax: number;
  vay: number;
  vbx: number;
  vby: number;
  vwx: number;
  vwy: number;
}

/** computeTrueWind — apparent → true (Python wind_utils.computeTrueWind). */
export function computeTrueWind(
  aws: number,
  awa: number,
  stw: number,
  hdg: number,
  lwy: number
): TrueWind {
  const awaRad = toRad(signedAwa(awa));
  const lwyRad = toRad(lwy * -1);

  const vax = aws * Math.cos(awaRad);
  const vay = aws * Math.sin(awaRad);
  const vbx = stw * Math.cos(lwyRad);
  const vby = stw * Math.sin(lwyRad);

  const vwx = vax - vbx;
  const vwy = vay - vby;

  const tws = Math.hypot(vwx, vwy);
  const twa = toDeg(Math.atan2(vwy, vwx));
  const twd = wrap360(angleAdd(hdg, twa));

  return { tws, twa, twd, vax, vay, vbx, vby, vwx, vwy };
}

export interface ApparentWind {
  aws: number;
  awa: number;
  vax: number;
  vay: number;
  vbx: number;
  vby: number;
  vwx: number;
  vwy: number;
}

/**
 * Inverse of computeTrueWind: true wind + boat → apparent (same boat frame).
 * Va = Vw + Vb
 */
export function computeApparentFromTrue(
  tws: number,
  twa: number,
  stw: number,
  lwy: number
): ApparentWind {
  const twaRad = toRad(twa);
  const lwyRad = toRad(lwy * -1);

  const vwx = tws * Math.cos(twaRad);
  const vwy = tws * Math.sin(twaRad);
  const vbx = stw * Math.cos(lwyRad);
  const vby = stw * Math.sin(lwyRad);

  const vax = vwx + vbx;
  const vay = vwy + vby;

  const aws = Math.hypot(vax, vay);
  const awa = toDeg(Math.atan2(vay, vax));

  return { aws, awa, vax, vay, vbx, vby, vwx, vwy };
}

// ---------------------------------------------------------------------------
// The bias model
// ---------------------------------------------------------------------------

/**
 * Offsets applied to measured |AWA| (simulate mode):
 *   awa_stbd = +|awa| + offset + mastRot
 *   awa_port = −|awa| + offset − mastRot
 */
export function awaWithOffsets(
  absAwa: number,
  tack: Tack,
  sensorOffset: number,
  mastRotOffset: number
): number {
  const mag = Math.abs(absAwa);
  if (tack === "stbd") {
    return wrap180(mag + sensorOffset + mastRotOffset);
  }
  return wrap180(-mag + sensorOffset - mastRotOffset);
}

/** Correct observed AWA by subtracting the bias model. */
export function awaCorrected(
  awaMeas: number,
  tack: Tack,
  sensorOffset: number,
  mastRotOffset: number
): number {
  if (tack === "stbd") {
    return wrap180(awaMeas - sensorOffset - mastRotOffset);
  }
  return wrap180(awaMeas - sensorOffset + mastRotOffset);
}

/**
 * Leeway is drift to leeward, so which way it points depends on the tack: to
 * port on starboard tack, to starboard on port tack. In this tool's HDG−COG
 * convention (+ = drift to port) that means the sign flips with the tack. One
 * signed value applied to both tacks puts the boat vector on the wrong side of
 * the bow for one of them — an error of 2·|leeway|, the same size as the
 * biases being fitted.
 */
export function tackLeeway(lwy: number, tack: Tack): number {
  const mag = Math.abs(lwy);
  return tack === "stbd" ? mag : -mag;
}

// ---------------------------------------------------------------------------
// Root finding
// ---------------------------------------------------------------------------

/**
 * Bisect a strictly monotone residual to its root. Returns null when the
 * bracket holds no sign change, which is a real answer — the correction
 * cannot reach the target — and never a silently clamped edge value.
 */
export function bisectRoot(
  f: (x: number) => number,
  lo: number,
  hi: number
): number | null {
  let a = lo;
  let b = hi;
  let fa = f(a);
  const fb = f(b);
  if (!Number.isFinite(fa) || !Number.isFinite(fb)) {return null;}
  if (fa === 0) {return a;}
  if (fb === 0) {return b;}
  if (fa > 0 === fb > 0) {return null;}

  for (let i = 0; i < 200 && b - a > 1e-12; i++) {
    const mid = (a + b) / 2;
    const fm = f(mid);
    // A hole in the residual (an inner solve with no root) means the bracket
    // is not one continuous branch, so bisecting it would be meaningless.
    if (!Number.isFinite(fm)) {return null;}
    if (fm === 0) {return mid;}
    if (fm > 0 === fa > 0) {
      a = mid;
      fa = fm;
    } else {
      b = mid;
    }
  }
  return (a + b) / 2;
}

/**
 * Scan a residual across a range, then bisect the first sign change found.
 *
 * The outer (mast) residual is only defined where the inner offset solve has a
 * root, so the range contains holes — and the extreme ends are usually holes,
 * because a large trial mast pushes the offset it needs past its own limit.
 * Assuming the endpoints form a valid bracket therefore fails on perfectly
 * ordinary inputs. Sampling first finds a real bracket inside the valid region.
 *
 * Returns { root, spread }: spread is the residual's total variation over the
 * valid region, which is how flatness (an unidentifiable parameter) is
 * distinguished from a genuine root.
 */
export function scanForRoot(
  f: (x: number) => number,
  lo: number,
  hi: number,
  steps = 240
): { root: number | null; spread: number } {
  let prevX: number | null = null;
  let prevY: number | null = null;
  let root: number | null = null;
  let min = Infinity;
  let max = -Infinity;

  for (let i = 0; i <= steps; i++) {
    const x = lo + ((hi - lo) * i) / steps;
    const y = f(x);
    if (!Number.isFinite(y)) {
      prevX = null;
      prevY = null;
      continue;
    }
    if (y < min) {min = y;}
    if (y > max) {max = y;}
    if (root === null) {
      if (y === 0) {
        root = x;
      } else if (prevY !== null && prevY > 0 !== y > 0) {
        root = bisectRoot(f, prevX as number, x);
      }
    }
    prevX = x;
    prevY = y;
  }

  return { root, spread: max >= min ? max - min : 0 };
}

/**
 * Every root of a residual across a range, not just the first.
 *
 * Downwind the true wind is a small difference between two larger vectors — a
 * foiling boat sails deep at well over the wind speed, so |Va| and |Vb| are both
 * far bigger than |Vw| — which makes TWA extremely sensitive to AWA and turns
 * these residuals non-monotone. A bracket that is one clean crossing upwind can
 * hold several crossings downwind, most of them physically absurd.
 */
export function scanForAllRoots(
  f: (x: number) => number,
  lo: number,
  hi: number,
  steps = 240
): { roots: number[]; spread: number } {
  let prevX: number | null = null;
  let prevY: number | null = null;
  const roots: number[] = [];
  let min = Infinity;
  let max = -Infinity;

  for (let i = 0; i <= steps; i++) {
    const x = lo + ((hi - lo) * i) / steps;
    const y = f(x);
    if (!Number.isFinite(y)) {
      prevX = null;
      prevY = null;
      continue;
    }
    if (y < min) {min = y;}
    if (y > max) {max = y;}
    if (y === 0) {
      roots.push(x);
    } else if (prevY !== null && prevY !== 0 && prevY > 0 !== y > 0) {
      const r = bisectRoot(f, prevX as number, x);
      if (r !== null) {roots.push(r);}
    }
    prevX = x;
    prevY = y;
  }

  return { roots, spread: max >= min ? max - min : 0 };
}

/**
 * The root closest to zero.
 *
 * A calibration is a correction to a sensor that is meant to be roughly right,
 * so of several values that fit the data the smallest is the only defensible
 * one. Taking the first crossing found while sweeping from the negative end
 * instead is what produced a −24° mast rotation downwind: it satisfied the angle
 * criterion exactly while tearing the two tacks' |AWA| apart by 40°.
 */
export function scanForRootNearestZero(
  f: (x: number) => number,
  lo: number,
  hi: number,
  steps = 240
): { root: number | null; spread: number } {
  const { roots, spread } = scanForAllRoots(f, lo, hi, steps);
  if (roots.length === 0) {return { root: null, spread };}
  let best = roots[0];
  for (const r of roots) {
    if (Math.abs(r) < Math.abs(best)) {best = r;}
  }
  return { root: best, spread };
}

// ---------------------------------------------------------------------------
// Evaluation and the fit
// ---------------------------------------------------------------------------

/** One tack's apparent wind plus the boat speed it was sailed at. */
export interface TackApparent extends ApparentWind {
  bsp: number;
}

export interface CorrectedPair {
  port: TrueWind & { awa: number; aws: number };
  stbd: TrueWind & { awa: number; aws: number };
  dTwa: number;
  dTws: number;
  meanTws: number;
  tackAngle: number;
}

export function evalCorrected(
  portAw: TackApparent,
  stbdAw: TackApparent,
  off: number,
  mast: number,
  hdg: number,
  lwy: number
): CorrectedPair {
  const awaP = awaCorrected(portAw.awa, "port", off, mast);
  const awaS = awaCorrected(stbdAw.awa, "stbd", off, mast);
  const port = computeTrueWind(portAw.aws, awaP, portAw.bsp, hdg, tackLeeway(lwy, "port"));
  const stbd = computeTrueWind(stbdAw.aws, awaS, stbdAw.bsp, hdg, tackLeeway(lwy, "stbd"));
  return {
    port: { ...port, awa: awaP, aws: portAw.aws },
    stbd: { ...stbd, awa: awaS, aws: stbdAw.aws },
    dTwa: Math.abs(port.twa) - Math.abs(stbd.twa),
    dTws: port.tws - stbd.tws,
    meanTws: (port.tws + stbd.tws) / 2,
    tackAngle: Math.abs(port.twa) + Math.abs(stbd.twa),
  };
}

/** One tack as observed on the boat: what the instruments displayed. */
export interface ObservedTack {
  bsp: number;
  twa: number;
  tws: number;
}

export interface RecommendInput {
  port: ObservedTack;
  stbd: ObservedTack;
  hdg: number;
  lwy: number;
  correction: string;
  /** Trusted absolute TWS, or null when none is available. */
  refTws: number | null;
}

export interface Recommendation {
  off: number;
  mast: number;
  offSolved: boolean;
  mastState: MastState;
  mastSensitivity: number | null;
  mastUncertainty: number | null;
  mastFromReference: boolean;
  refTws: number | null;
  portAw: TackApparent;
  stbdAw: TackApparent;
  before: CorrectedPair;
  after: CorrectedPair;
  correction: string;
  mode: CorrectionMode;
}

/**
 * Recover the bias from an observed port/stbd pair.
 *
 * Solved as a NESTED pair of 1-D root finds rather than a joint search over a
 * cost function that would have to trade knots against degrees at some made-up
 * exchange rate:
 *
 *   inner  offset ← Δ|TWA| = 0, re-solved exactly for every candidate mast.
 *                   Strictly monotone in the offset and brackets zero.
 *   outer  mast   ← a TWS condition, evaluated on the inner solution:
 *                     with a reference TWS   → mean TWS = reference
 *                     without one            → ΔTWS = 0 (the tack split)
 *
 * Nesting matters: alternating the two solves independently stalls, because
 * each re-solve invalidates the other's assumption. Re-solving the offset
 * inside the outer residual means the outer function is always evaluated on a
 * genuinely angle-matched pair.
 */
export function recommendOffsets(rec: RecommendInput): Recommendation {
  const mode = correctionMode(rec.correction);
  const portAw: TackApparent = {
    ...computeApparentFromTrue(
      rec.port.tws,
      rec.port.twa,
      rec.port.bsp,
      tackLeeway(rec.lwy, "port")
    ),
    bsp: rec.port.bsp,
  };
  const stbdAw: TackApparent = {
    ...computeApparentFromTrue(
      rec.stbd.tws,
      rec.stbd.twa,
      rec.stbd.bsp,
      tackLeeway(rec.lwy, "stbd")
    ),
    bsp: rec.stbd.bsp,
  };

  const at = (off: number, mast: number) =>
    evalCorrected(portAw, stbdAw, off, mast, rec.hdg, rec.lwy);
  const before = at(0, 0);

  const refTws = mode.fitMast ? rec.refTws : null;

  /** Inner solve: the offset that makes |TWA| agree, for a given mast. */
  const offsetFor = (mast: number) =>
    bisectRoot((v) => at(v, mast).dTwa, -BIAS_LIMIT_DEG, BIAS_LIMIT_DEG);

  /**
   * Outer residual, as a function of mast alone. NaN where the inner solve has
   * no root, so the outer scan cannot wander into that region.
   */
  const mastResidual = (mast: number) => {
    const off = offsetFor(mast);
    if (off === null) {return NaN;}
    const ev = at(off, mast);
    return refTws !== null ? ev.meanTws - refTws : ev.dTws;
  };

  let mast = 0;
  let mastState: MastState = "notApplicable";

  if (mode.fitMast) {
    const scan = scanForRoot(mastResidual, -BIAS_LIMIT_DEG, BIAS_LIMIT_DEG);

    // Flat residual: every mast value fits equally well. That is the
    // equal-speed case with no reference TWS, and it must be reported rather
    // than resolved — a root find on a flat function returns whichever point
    // it happened to test first and dresses it up as an answer.
    if (scan.spread < TWS_UNCERTAINTY) {
      mastState = refTws !== null ? "unreachable" : "unidentifiable";
    } else if (scan.root === null) {
      mastState = refTws !== null ? "unreachable" : "noSplitSolution";
    } else {
      mast = scan.root;
      mastState = "fitted";
    }
  }

  const solvedOff = offsetFor(mast);
  const offSolved = solvedOff !== null;
  const off = offSolved ? (solvedOff as number) : 0;
  const after = at(off, mast);

  // How firmly is the mast rotation actually pinned? Differentiate the outer
  // residual at the solution: a shallow slope means a tiny TWS error moves the
  // answer a long way, which the user needs to know before trusting it.
  let mastSensitivity: number | null = null;
  let mastUncertainty: number | null = null;
  if (mastState === "fitted") {
    const h = 0.05;
    const hi = mastResidual(mast + h);
    const lo = mastResidual(mast - h);
    if (Number.isFinite(hi) && Number.isFinite(lo)) {
      mastSensitivity = (hi - lo) / (2 * h);
      if (Math.abs(mastSensitivity) > 1e-9) {
        // Degrees of mast error produced by TWS_UNCERTAINTY of speed error.
        mastUncertainty = Math.abs(TWS_UNCERTAINTY / mastSensitivity);
      }
    }
  }

  return {
    off,
    mast,
    offSolved,
    mastState,
    mastSensitivity,
    mastUncertainty,
    mastFromReference: refTws !== null,
    refTws,
    portAw,
    stbdAw,
    before,
    after,
    correction: rec.correction,
    mode,
  };
}

// ---------------------------------------------------------------------------
// Fitting from measured apparent wind (the live CALIBRATION page)
// ---------------------------------------------------------------------------

/**
 * Residual below which the target-TWA solve counts as flat, in degrees of TWA.
 *
 * The mast-rotation residual against a target is far better conditioned than the
 * TWS-split one — a degree of rotation moves |TWA| by about a degree — so this
 * is a tolerance on the same quantity being fitted, not a speed uncertainty.
 */
export const TWA_TARGET_TOLERANCE_DEG = 0.05;

/** Mean |TWA| across the tacks — the quantity a target TWA is compared against. */
export function meanAbsTwa(pair: CorrectedPair): number {
  return (Math.abs(pair.port.twa) + Math.abs(pair.stbd.twa)) / 2;
}

/**
 * The sensor offset that makes |TWA| agree across the tacks, for a given mast
 * rotation, from MEASURED apparent wind.
 *
 * recommendOffsets takes what the instruments DISPLAYED (true wind) and rebuilds
 * the apparent wind behind it. On a boat logging raw vane data there is nothing
 * to rebuild — Awa_bow_deg and Aws_bow_kph are already the apparent wind — so
 * going through the true-wind form would only invert and re-apply the same
 * triangle. Heading does not enter Δ|TWA| at all; pass 0.
 */
export function solveOffsetFromApparent(
  portAw: TackApparent,
  stbdAw: TackApparent,
  mast: number,
  lwy: number
): number | null {
  // Scan rather than bisect the whole bracket: downwind Δ|TWA| is not monotone in
  // the offset, and bisection over a range holding several crossings converges on
  // whichever one the halving happens to trap. The smallest correction wins.
  return scanForRootNearestZero(
    (v) => evalCorrected(portAw, stbdAw, v, mast, 0, lwy).dTwa,
    -BIAS_LIMIT_DEG,
    BIAS_LIMIT_DEG
  ).root;
}

/**
 * The sensor offset read straight off the measured |AWA| split.
 *
 * The bias model is, in magnitudes,
 *
 *     |awa_stbd| = |awa| + offset + mast
 *     |awa_port| = |awa| − offset + mast
 *
 * so the split IS twice the offset and the mast rotation cancels out of it
 * entirely. No triangle, no root find, no branch to pick wrong.
 *
 * This is the observable to fit the offset on, and solveOffsetFromApparent
 * (Δ|TWA| → 0) is not, for a reason that only shows up on real data: |AWA|
 * depends on boat speed, so the two sides must be compared at matched speed —
 * see aggregateTacks. Once they are, the angle the sensor reports is a direct
 * measurement of the bias, whereas TWA is that same measurement pushed through a
 * subtraction of two nearly equal vectors, which downwind amplifies both the
 * noise and any residual speed mismatch enormously. Matching production's
 * cal_utils, which likewise compares |AWA| at matched (Bsp, Tws) and halves it.
 */
export function offsetFromAwaSplit(portAw: TackApparent, stbdAw: TackApparent): number {
  const port = wrap180(portAw.awa);
  const stbd = wrap180(stbdAw.awa);
  // On port tack the wind must read from the port side and on starboard from the
  // starboard side. If it does not, the vane is reporting both tacks on the same
  // side of the bow: the magnitude model above no longer holds, and halving the
  // split would return a confident, wrong, small number. That is a wiring or
  // convention error, not a calibration.
  if (!(port < 0 && stbd > 0)) {return NaN;}
  return (Math.abs(stbd) - Math.abs(port)) / 2;
}

export interface MastTargetSolution {
  mast: number | null;
  off: number | null;
  state: MastState;
  spread: number;
  sensitivity: number | null;
}

/**
 * Fit the mast rotation so the corrected mean |TWA| lands on a target angle.
 *
 * Mast rotation is symmetric across the tacks, so the tack split cannot see it
 * (see CORRECTION_MODES): it needs an external reference. Where the tool uses a
 * trusted TWS, this uses the target TWA the team model publishes as
 * Twa_tgt_deg — the level of |TWA| moves directly and strongly with rotation.
 *
 * Nested, like recommendOffsets: the offset is re-solved inside every trial
 * mast, so the outer residual is always evaluated on an angle-matched pair.
 */
export function solveMastForTargetTwa(
  portAw: TackApparent,
  stbdAw: TackApparent,
  targetAbsTwa: number,
  lwy: number
): MastTargetSolution {
  // The offset comes from the |AWA| split, which the mast rotation cannot move
  // (it is symmetric across the tacks), so it is a constant here rather than an
  // inner solve. That independence is what makes the two parameters separable.
  const off = offsetFromAwaSplit(portAw, stbdAw);
  const offsetFor = (_mast: number) => off;

  const residual = (mast: number): number =>
    meanAbsTwa(evalCorrected(portAw, stbdAw, off, mast, 0, lwy)) - targetAbsTwa;

  const scan = scanForRootNearestZero(residual, -BIAS_LIMIT_DEG, BIAS_LIMIT_DEG);

  if (scan.spread < TWA_TARGET_TOLERANCE_DEG) {
    // |TWA| barely responds to rotation over the whole bracket, so any answer
    // would be the first point tested rather than a fit.
    return { mast: null, off: offsetFor(0), state: "unidentifiable", spread: scan.spread, sensitivity: null };
  }
  if (scan.root === null) {
    // The residual moves but never crosses zero: the target is outside what this
    // correction can reach, which is a real answer and not a reason to clamp.
    return { mast: null, off: offsetFor(0), state: "unreachable", spread: scan.spread, sensitivity: null };
  }

  const mast = scan.root;
  const h = 0.05;
  const hi = residual(mast + h);
  const lo = residual(mast - h);
  const sensitivity =
    Number.isFinite(hi) && Number.isFinite(lo) ? (hi - lo) / (2 * h) : null;

  return { mast, off: offsetFor(mast), state: "fitted", spread: scan.spread, sensitivity };
}

export interface ApparentRecommendInput {
  port: TackApparent;
  stbd: TackApparent;
  lwy: number;
  correction: string;
  /** Target |TWA| for the mast fit, or null when the window carries no target. */
  targetAbsTwa: number | null;
  /** Operator-supplied mast rotation. When set, the offset is re-solved for it instead of fitting. */
  mastOverride?: number | null;
}

export interface ApparentRecommendation {
  off: number;
  offSolved: boolean;
  mast: number;
  mastState: MastState;
  mastSensitivity: number | null;
  targetAbsTwa: number | null;
  before: CorrectedPair;
  after: CorrectedPair;
  correction: string;
  mode: CorrectionMode;
}

/**
 * Recover the bias of one sensor from a measured port/stbd apparent-wind pair.
 *
 *   bow — hull-mounted, so a constant offset is the only free parameter.
 *   mhu — rides the rotating mast, so a rotation-coupled term sits on top; it is
 *         pinned by the target TWA, or reported as unidentifiable without one.
 *
 * The offset is measured, not searched: it is half the |AWA| split (see
 * offsetFromAwaSplit), which assumes the caller has matched the two tacks on boat
 * speed. The mast rotation cannot move that split, so the two are independent and
 * the fit needs no nesting.
 */
export function recommendOffsetsFromApparent(
  rec: ApparentRecommendInput
): ApparentRecommendation {
  const mode = correctionMode(rec.correction);
  const at = (off: number, mast: number) =>
    evalCorrected(rec.port, rec.stbd, off, mast, 0, rec.lwy);
  const before = at(0, 0);

  let mast = 0;
  let mastState: MastState = "notApplicable";
  let mastSensitivity: number | null = null;

  if (mode.fitMast) {
    if (rec.mastOverride != null && Number.isFinite(rec.mastOverride)) {
      mast = rec.mastOverride;
      mastState = "fitted";
    } else if (rec.targetAbsTwa != null && Number.isFinite(rec.targetAbsTwa)) {
      const solved = solveMastForTargetTwa(rec.port, rec.stbd, rec.targetAbsTwa, rec.lwy);
      mastState = solved.state;
      mastSensitivity = solved.sensitivity;
      if (solved.mast != null) {mast = solved.mast;}
    } else {
      mastState = "unidentifiable";
    }
  }

  const measuredOff = offsetFromAwaSplit(rec.port, rec.stbd);
  // Beyond the limit this is not a calibration any more, and saying so beats
  // reporting a number nobody should apply.
  const offSolved = Number.isFinite(measuredOff) && Math.abs(measuredOff) <= BIAS_LIMIT_DEG;
  const off = offSolved ? measuredOff : 0;

  return {
    off,
    offSolved,
    mast,
    mastState,
    mastSensitivity,
    targetAbsTwa: rec.targetAbsTwa,
    before,
    after: at(off, mast),
    correction: rec.correction,
    mode,
  };
}

// ---------------------------------------------------------------------------
// Simulate mode
// ---------------------------------------------------------------------------

export interface SimulateInput {
  bsp: number;
  awaMeas: number;
  aws: number;
  hdg: number;
  lwy: number;
  sensorOffset: number;
  mastRotOffset: number;
}

/** One tack's full solution, as the readout tables and polar consume it. */
export interface TackSolution {
  tack: Tack;
  awa: number;
  aws: number;
  tws: number;
  twa: number;
  twd: number;
  vectors: TrueWind;
}

export function solveTack(
  inputs: SimulateInput,
  tack: Tack,
  useOffsets: boolean
): TackSolution {
  const off = useOffsets ? inputs.sensorOffset : 0;
  const mast = useOffsets ? inputs.mastRotOffset : 0;
  const awa = awaWithOffsets(inputs.awaMeas, tack, off, mast);
  const tw = computeTrueWind(
    inputs.aws,
    awa,
    inputs.bsp,
    inputs.hdg,
    tackLeeway(inputs.lwy, tack)
  );
  return {
    tack,
    awa,
    aws: inputs.aws,
    tws: tw.tws,
    twa: tw.twa,
    twd: tw.twd,
    vectors: tw,
  };
}

/** Package a computed true wind as a TackSolution for the shared readouts. */
export function packSolution(
  tack: Tack,
  tw: TrueWind,
  awa: number,
  aws: number
): TackSolution {
  return {
    tack,
    awa,
    aws,
    tws: tw.tws,
    twa: tw.twa,
    twd: tw.twd,
    vectors: tw,
  };
}
